import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/StatusBadge';
import { api } from '@/lib/api';
import { formatAgo } from '@/lib/ago';
import { usePolledData } from '@/lib/hooks/usePolledData';
import type { EndpointTLSStatus } from '@/types/tlsSettings';

const LABELS: Record<EndpointTLSStatus, string> = {
  ready: 'Ready',
  outdated: 'Outdated',
  error: 'Error',
  not_configured: 'Not configured',
};

function when(iso: string | null) {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return <span className="text-muted-foreground">Never</span>;
  return (
    <time dateTime={new Date(t).toISOString()} title={new Date(t).toLocaleString()}>
      {formatAgo(t)}
    </time>
  );
}

/** Origin-TLS state of one endpoint. Loads independently and never blocks the page. */
export function TlsPanel({ endpointId }: { endpointId: number }) {
  const { data, error, loading } = usePolledData(() => api.settings.tls.get(), 60_000);
  if (loading && !data) return null;
  if (error && !data) {
    return <p className="text-sm text-muted-foreground">TLS state unavailable.</p>;
  }
  // The TLS list only includes endpoints that have Traefik: absent means "not applicable".
  const entry = data?.endpoints.find((e) => e.endpoint_id === endpointId);
  if (!entry) return null;
  return (
    <Card className="min-w-0 gap-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">Origin TLS</h2>
        <StatusBadge status={entry.effective_status} label={LABELS[entry.effective_status] ?? entry.effective_status} />
      </div>
      <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">Apps using origin TLS</dt>
          <dd className="tabular-nums font-medium">{entry.origin_apps}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Last pushed</dt>
          <dd>{when(entry.pushed_at)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Last checked</dt>
          <dd>{when(entry.checked_at)}</dd>
        </div>
      </dl>
      <div className="space-y-1 text-sm">
        <p className="text-muted-foreground">Origin certificates served</p>
        {entry.ready_domains.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5" aria-label="Domains served">
            {entry.ready_domains.map((d) => (
              <li key={d}>
                <Badge variant="secondary" className="font-mono">{d}</Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p>None</p>
        )}
      </div>
      {entry.error ? (
        <p className="text-sm text-status-danger">
          <span className="font-medium">Error: </span>
          {entry.error}
        </p>
      ) : null}
      <Link
        to="/settings/domains"
        className="w-fit rounded text-sm underline underline-offset-4 outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Domain settings
      </Link>
    </Card>
  );
}
